<template>
    <LoadingDialog :visible="actionIsBeingExecuted" />

    <ConfirmationModal 
      :title="currentConfirmationData.title"
      :text="currentConfirmationData.text"
      :visible="confirmationModalVisibility"
      @emitResponse="confirmationResponse"
    />
    <LoaderComponent class="col-span-5 text-center pt-5 mt-5" v-if="tabLoading" />
    <div class="content-wrapper col-span-5 md:col-span-3 xl:col-span-2 flex flex-col gap-y-5" v-if="!tabLoading">
        <section class="w-full p-4 md:p-6 bg-white shadow-md rounded-md border border-iron">
            <h1 class="mb-6 text-lg font-semibold">{{ translate('driving licenses', 'ucfirst') }}</h1>
            <form
                class="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-6 text-base font-medium"
            >
                <span class="col-span-3 p-float-label">
                    <AutoComplete
                        v-model="drivingLicensesName" 
                        :suggestions="drivingLicensesList" 
                        optionLabel="name"
                        @complete="searchForLicenses"
                        :placeholder="`${translate('type to search')}...`"
                        :emptyMessage="translate('no results found', 'ucfirst')"
                        :emptySearchMessage="translate('no results found', 'ucfirst')"
                        :inputClass="'w-full'"
                        multiple
                        fluid 
                        dropdown
                        class="w-full rounded border border-iron autocomplete-w-full"
                    />
                </span>
                <hr class="iron pt-2 pb-2 col-span-3">
                <div class="col-span-3 flex flex-wrap">
                    <div v-for="(license, index) of drivingLicensesName" :class="`w-full mb-3 md:pe-3 md:w-1/3 license-opt-${license.code}-${license.name.replace(' ', 'aaaa-aaaa-aaaa')}`" :key="index">
                        <div class="rounded-full" style="border: 1px solid #cbd5e1;padding: 0.5rem 0.75rem!important; font-size: 1rem;">{{ license.name }}</div>
                    </div>
                </div>
            </form>
        </section>
    </div>
    <ActionCard 
        v-if="!tabLoading"
        :jobId="jobId"
        :jobStatus="props.jobStatus"
        :actionButtonText="translate('save driving licenses', 'ucfirst')"
        @emitClick="openConfirmationModal('drivingLicenseSave')"
        class="col-span-3 md:col-span-2 xl:col-span-1 flex flex-col gap-y-5 sticky top-24 h-[calc(100vh-theme(spacing.20))]"
    />
</template>

<script setup>
    import { ref, onMounted, watch, reactive, onBeforeMount } from "vue";
    import requestHandler from "@/composables/requestHandler";
    import AutoComplete from "primevue/autocomplete";
    import { translate } from "@/composables/translate";
    import { sleep } from "@/composables/sleep";
    import store from "@/store";
    import ActionCard from "@/components/Dashboard/Company/SaveJob/ActionCard"
    import ConfirmationModal from "@/components/Utils/ConfirmationModal";
    import jobData from "@/composables/jobData";
    import LoadingDialog from "@/components/Utils/LoadingDialog.vue";
    import { showToast } from "@/composables/toast";
    import { useToast } from "primevue/usetoast";
    import LoaderComponent from "@/components/Utils/LoaderComponent.vue";

    let toast = useToast();

    const props = defineProps({
        jobId: {
            type: String,
            required: false,
            default: null
        },
        jobStatus: {
            type: String,
            required: false,
            default: 'draft'
        }
    });

    let tabLoading = ref(true);
    let actionIsBeingExecuted = ref(false);
    let jobId = ref(null);
    onMounted(function(){
        handleAutoCompleteElements();
        getEssentialData();
    });

    onBeforeMount(function(){
        jobId.value = props.jobId;
        if(jobId.value == null)
            goToJobPage();
    });

    async function getEssentialData(){
        tabLoading.value = true;
        await feedDrivingLicensesCombo();
        await fetchAndFeedDrivingLicenses();
        tabLoading.value = false;
    }

    async function fetchAndFeedDrivingLicenses(){
        if(!jobId.value)
            return;
        let values = await jobData.searchDrivingLicenses(jobId.value);
        drivingLicensesName.value = values.drivingLicense;
    }

    let drivingLicenseCombo = reactive({
        data: [],
        selected: [],
        loading: []
    })
    async function feedDrivingLicensesCombo(){
        drivingLicenseCombo.loading = true;
        const result = await jobData.getJobComboData('job_driving_license');
        drivingLicenseCombo.data = result;
        drivingLicenseCombo.loading = false;
    }

    async function goToJobPage(){
        await requestHandler.redirect(`/dashboard/company/${store.getters.getUserSlug}/post-job`);
    }

    watch(() => props.jobId, (newValue) => {
      jobId.value = newValue;
      handleAutoCompleteElements();
    });

    
    let confirmationModalVisibility = ref(false);
    const confirmationData = {
        drivingLicenseSave: {
            title: translate('are you sure?', 'ucfirst'),
            text: translate('do you wish to save these driving licenses? This action will make your job unavaliable until it passes by our validation trials', 'ucfirst')
        }
    }
    let currentConfirmationData = reactive({
        title: '',
        text: '',
        action: ''
    })
    function openConfirmationModal(action = ''){
        currentConfirmationData.action = '';
        confirmationModalVisibility.value = false;
        if(action == '' || !['drivingLicenseSave'].includes(action))
            return;
        currentConfirmationData.action = action;
        currentConfirmationData.title = confirmationData[action].title;
        currentConfirmationData.text  = confirmationData[action].text;
        confirmationModalVisibility.value = true;
    }

    let drivingLicensesName = ref([]);
    let drivingLicensesList = ref([]);
    async function searchForLicenses(event){
      sleep(500);
      drivingLicensesList.value = [];
      let term = event.query.toLowerCase();
      let matches = [];
      for(let lang of drivingLicenseCombo.data){
        let undercase = lang.name.toLowerCase();
        if(undercase.indexOf(term) != -1){
            matches.push(lang);
        }
      }
      drivingLicensesList.value = matches;
    }
 
    async function handleAutoCompleteElements(){
      let tries = 0;
      let autocompleteEls = document.querySelectorAll('.autocomplete-w-full');
      while(autocompleteEls.length < 1){
          autocompleteEls = document.querySelectorAll('.autocomplete-w-full');
          tries++;
          await sleep(50);
          if(tries == 30){
            break;
          }
      }
      if(autocompleteEls.length < 1)
          return;
      autocompleteEls.forEach(el => {
        if(el.style.width == '')
          el.style.setProperty('width', '100%', 'important');
          const ulEl = el.querySelector('ul');
          if(ulEl){
            ulEl.style.setProperty('width', '100%', 'important');
            // ulEl.style.setProperty('border-radius', '9999px', 'important');
            let inputEl = ulEl.querySelector('input');
          if(inputEl){
            inputEl.style.setProperty('width', '100%', 'important');
            inputEl.style.setProperty('outline', 'unset', 'important');
          }
        }
      });
    }

    async function confirmationResponse(result){
        confirmationModalVisibility.value = false;
        if(result.confirmation){
            actionIsBeingExecuted.value = true;
            switch(currentConfirmationData.action){
                case 'drivingLicenseSave':
                    await saveDrivingLicense();
                break;
            }
            actionIsBeingExecuted.value = false;
        }
        currentConfirmationData.action = '';
    }

    async function saveDrivingLicense(){
        let licensesIds   = [];
        let licensesName = [];
        for(let data of drivingLicensesName.value){
            licensesIds.push(data['code']);
            licensesName.push(data['name']);
        }
        const response = await requestHandler.executeRequest(`api/joblist/managedrivinglicense/${jobId.value}`, {
            action: 'sync', driving_licenses_ids: licensesIds, driving_licenses_names: licensesName
        }, 'post');
        if(!response.success){
            let errorMessage = licensesIds.length > 1 ? translate('driving licenses not saved') : translate('driving license not saved');
            showToast(toast, errorMessage, 'error');
            return;
        }
        let successMessage = licensesIds.length > 1 ? translate('driving licenses saved') : translate('driving license saved');
        showToast(toast, successMessage);
        await jobData.updateMyCompanyJobs(jobId.value, response.data.data);
        store.dispatch('saveOnDataChest', {
            key: `save_job_${jobId.value}`,
            value: response.data.data
        });
    }
</script>